/* eslint-disable css-modules/no-undef-class */
import React from 'react';
import { GetStaticProps, NextPage } from 'next';
import Link from 'next/link';
import { DESIGNER, PRODUCER } from 'routes';

import { getProductDetail } from 'api';
import Block from 'components/Block';
import Button from 'components/Button';
import Clickable from 'components/Clickable';
import Container from 'components/Container';
import DownloadTearSheet from 'components/DownloadTearSheet';
import Image from 'components/Image';
import { LazyLightbox } from 'components/Lightbox';
import MetaDescription from 'components/MetaDescription';
import MetaTitle from 'components/MetaTitle';
import Spaced from 'components/Spaced';
import Typography from 'components/Typography';
import Layout from 'containers/Layout';
import Main from 'containers/Main';
import { LazyMoreByDesigner } from 'containers/MoreByDesigner';
import ProductSizes from 'containers/ProductSizes';
import { useEnquiries } from 'contexts/EnquiryContext';
import ChevronDown from 'icons/chevron-down.svg';
import styles from 'styles/pages/Product.module.css';
import { Product } from 'types/Product';
import { cx, joinElements, makeStringParam, truncate } from 'utils';
import makeDevImageSrc from 'utils/makeDevImgSrc';

export type ProductProps = {
  product: Product;
};

const DEFAULT_LIGHTBOX_STATE = { open: false, startIndex: 0 };

const ProductDetail: NextPage<ProductProps> = ({ product }) => {
  const {
    pictures,
    name,
    read_more_description,
    description,
    designers,
    year,
    year_decade,
    browse_thumb,
    browse_thumb_x2,
    ref,
    brand,
    status,
  } = product;
  const { addProduct } = useEnquiries();
  const [lightboxState, setLightboxState] = React.useState(DEFAULT_LIGHTBOX_STATE);
  const [showMore, setShowMore] = React.useState(false);

  const highResImages = pictures.map(({ image }) => makeDevImageSrc(image || ''));

  const renderZoomButton = (startIndex: number) => (
    <button
      className={styles.openLightbox}
      onClick={() => setLightboxState({ open: true, startIndex })}
    />
  );

  return (
    <>
      {lightboxState.open && (
        <LazyLightbox
          onClose={() => setLightboxState(DEFAULT_LIGHTBOX_STATE)}
          images={highResImages}
          open
          startIndex={lightboxState.startIndex}
        />
      )}
      <MetaTitle>
        {`${[designers.map(d => `${d.firstname} ${d.lastname}`).join(', '), name]
          .filter(Boolean)
          .join(' – ')}`}
      </MetaTitle>
      <MetaDescription image={browse_thumb_x2}>{truncate(description, 150)}</MetaDescription>
      {/** TODO: PRODUCT SCHEMA */}
      <Layout>
        <Main paddingTop>
          <Container className={styles.container}>
            <div className={styles.inner}>
              <section className={styles.images}>
                {pictures.map(({ image, image_960w, image_1920w }, index) => {
                  return (
                    <React.Fragment key={index}>
                      {image ? (
                        <figure className={styles.imageContainer}>
                          <Image
                            src={makeDevImageSrc(image_960w || '')}
                            srcSet={`${makeDevImageSrc(image_960w || '')} 960w, ${makeDevImageSrc(
                              image_1920w || '',
                            )} 1920w`}
                            alt={name}
                            aspectRatio="letterbox"
                          />

                          {renderZoomButton(index)}
                        </figure>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </section>
              <aside className={styles.aside}>
                <Spaced spacing={2} className={styles.content}>
                  <div>
                    <Typography component="h1" uppercase>
                      {name}
                    </Typography>
                    <Typography component="h2" uppercase>
                      {joinElements(
                        designers.map(designer => {
                          return (
                            <Link
                              key={designer.id}
                              href={{ pathname: DESIGNER.pathname, query: { slug: designer.slug } }}
                            >{`${designer.firstname} ${designer.lastname}`}</Link>
                          );
                        }),
                        <span>, </span>,
                      )}
                    </Typography>

                    <Typography component="h2" color="faded">
                      {brand && (
                        <>
                          Produced by{' '}
                          <Link
                            href={{ pathname: PRODUCER.pathname, query: { slug: brand.slug } }}
                            target="_blank"
                          >
                            {`${brand.name}`}
                          </Link>
                        </>
                      )}
                    </Typography>
                    <Typography component="h2" color="faded">
                      {/* Add 's' to the end if only the decade is specified (eg. 1980s vs 1980) */}
                      {year && `${year}${year_decade ? 's' : ''}`}
                    </Typography>
                  </div>

                  <Typography component="p" pre setInnerHtml>
                    {description}
                  </Typography>

                  {read_more_description && (
                    <>
                      <Button
                        endAdornment={
                          <ChevronDown className={cx(styles.arrow, showMore && styles.open)} />
                        }
                        disablePadding
                        color="transparent"
                        onClick={() => setShowMore(!showMore)}
                      >
                        <Typography variant="mid" component="p">
                          Read more
                        </Typography>
                      </Button>

                      <Block className={cx(styles.readMoreDescription, showMore && styles.open)}>
                        <Typography component="p" pre setInnerHtml>
                          {read_more_description}
                        </Typography>
                      </Block>
                    </>
                  )}

                  <div className={styles.delimiter} />

                  <Typography component="p">Ref: {ref}</Typography>

                  <div>
                    <Typography component="div" pre className={styles.sizes}>
                      <ProductSizes product={product} />
                    </Typography>
                    {status !== 2 ? (
                      <>
                        <DownloadTearSheet product={product} className={styles.cta} />

                        <Clickable onClick={() => addProduct([product.id])}>
                          <Button className={cx(styles.enqueryButton)}>
                            <Typography variant="button">Enquire</Typography>
                          </Button>
                        </Clickable>
                      </>
                    ) : (
                      <Typography className={cx(styles.sold, styles.ctaBig)} block>
                        SOLD
                      </Typography>
                    )}
                  </div>
                </Spaced>
              </aside>
              {browse_thumb && (
                <figure className={cx(styles.firstImage, styles.imageContainer)}>
                  <Image
                    src={makeDevImageSrc(browse_thumb)}
                    srcSet={`${makeDevImageSrc(browse_thumb)} 420w, ${makeDevImageSrc(
                      browse_thumb_x2,
                    )} 840w`}
                    alt={name}
                  />

                  {renderZoomButton(0)}
                </figure>
              )}
            </div>

            {designers.length > 0 && (
              <Block padding="none">
                <LazyMoreByDesigner product={product} />
              </Block>
            )}
          </Container>
        </Main>
      </Layout>
    </>
  );
};

export const getStaticProps: GetStaticProps = async ctx => {
  const { id } = ctx.params || {};

  try {
    if (!id) {
      return { notFound: true, revalidate: 60 * 5 };
    }
    const { data: product } = await getProductDetail({
      id: makeStringParam(id),
    });
    if (!product) {
      return { notFound: true, revalidate: 60 * 5 };
    }
    return {
      props: {
        product,
      },
      revalidate: 60 * 5,
    };
  } catch {
    return { notFound: true, revalidate: 60 * 5 };
  }
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default ProductDetail;
